import * as React from "react";

import { useSWRConfig } from "swr";

import {
  VerticalDivider,
  RefreshIcon,
  ClearAllIcon,
  SelectAllIcon,
  DeleteIcon,
} from "src/common/components/table/toolbarIcons";
import { ConfirmIconButton } from "src/common/components/ui/ConfirmButton";
import { PageRows } from "src/common/hooks/rowSelection";
import { isDeleteRequestInvalid } from "src/lib/table/disks";
import { getGenericTooltipType, TooltipType } from "src/lib/table/tooltip";

enum InvalidType {
  InUseDisks = 1,
}

function deleteTooltipMessage(tooltipType: TooltipType, invalidType: InvalidType) {
  const message = new Map<TooltipType, string>([
    [TooltipType.Deleting, "Deleting . . ."],
    [TooltipType.NoSelection, "Select disks to delete"],
    [TooltipType.ValidSelection, "Delete selected disks"],
  ]);

  if (tooltipType === TooltipType.Invalid) {
    // Invalid could be caused from multiple scenarios. This is a per table implementation
    // hence the InvalidType is not generalized or abstracted out.
    if (invalidType === InvalidType.InUseDisks) {
      return "Disks that are in-use cannot be deleted. Please uncheck any in-use disks.";
    }
  }

  return message.get(tooltipType);
}

export const CustomToolbar = ({
  rowStore,
  diskTableData,
  deleteDisk,
}: {
  rowStore: {
    get: () => PageRows;
    clear: () => void;
    checkAllForTable: () => void;
  };
  diskTableData: {
    diskStatus: string;
  }[];
  deleteDisk: (ids: Array<string>) => Promise<void>;
}) => {
  const [isDeleting, setIsDeleting] = React.useState(false);

  const { mutate } = useSWRConfig();

  const selectedRows = rowStore.get();

  const onDelete = async () => {
    const ids = Array.from(selectedRows.keys());
    setIsDeleting(true);
    await deleteDisk(ids);
    rowStore.clear();
    setIsDeleting(false);
  };

  // If delete is NOT allowed on any selected rows, make request invalid.
  const invalidDeleteRequest = React.useMemo(() => {
    return selectedRows?.size > 0 ? isDeleteRequestInvalid(diskTableData, selectedRows) : false;
  }, [selectedRows, diskTableData]);
  const disableDelete = React.useMemo(() => {
    return selectedRows?.size === 0 || isDeleting || invalidDeleteRequest;
  }, [selectedRows, isDeleting, invalidDeleteRequest]);

  const tooltipType = getGenericTooltipType(selectedRows?.size, invalidDeleteRequest, isDeleting);
  const message = deleteTooltipMessage(tooltipType, invalidDeleteRequest && InvalidType.InUseDisks);
  return (
    <>
      <VerticalDivider />
      <ClearAllIcon clear={rowStore.clear} />
      <SelectAllIcon checkAllForTable={rowStore.checkAllForTable} />
      <VerticalDivider />
      <ConfirmIconButton
        onSubmit={async () => await onDelete()}
        showModalFunc={() => true}
        confirmText={`Are you sure you want to delete ${selectedRows?.size} disks? Any saved work will be lost.`}
        confirmButtonText="Yes"
        modalSx={{
          borderRadius: "0px",
          position: "fixed",
        }}
        tooltip={message}
        icon={DeleteIcon(tooltipType)}
        name={`Delete selected disks`}
        title={`Delete selected disks`}
        type="submit"
        size="medium"
        disabled={disableDelete}
        color="error"
      />
      <RefreshIcon mutate={mutate} />
    </>
  );
};
